import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useRequete } from '../api/client';
import type { Recherche } from '../api/types';
import { useSession } from '../auth/session';
import { Anneau, Chargement, Equipes, Message, dateCourte } from '../composants/elements';

/**
 * « Qui connaît X » — §6.3.4, la requête de pré-vente. Avec la preuve et la
 * date de déclaration : une compétence déclarée il y a deux ans ne vaut pas
 * celle d'hier.
 */
export function QuiConnait(): JSX.Element {
  const session = useSession();
  const [parametres, setParametres] = useSearchParams();
  const [terme, setTerme] = useState(parametres.get('techno') ?? '');
  const [requete, setRequete] = useState(parametres.get('techno') ?? '');
  const [niveauMin, setNiveauMin] = useState(3);

  useEffect(() => {
    const depuisUrl = parametres.get('techno');
    if (depuisUrl && depuisUrl !== requete) {
      setTerme(depuisUrl);
      setRequete(depuisUrl);
    }
  }, [parametres, requete]);

  const resultat = useRequete<Recherche>(
    requete.trim().length >= 2
      ? `/api/recherche?techno=${encodeURIComponent(requete.trim())}&niveauMin=${niveauMin}`
      : null,
    [requete, niveauMin],
  );

  if (!session.estAuMoins('encadrement')) {
    return (
      <div className="contenu section">
        <Message variante="attention">
          Named search is restricted to management: it names people and quotes their evidence.
          Your own profile is always available under “My profile”.
        </Message>
      </div>
    );
  }

  return (
    <div className="contenu" style={{ paddingBottom: 'var(--space-9)' }}>
      <div className="entete-page">
        <div>
          <p className="eyebrow" style={{ marginBottom: 14 }}>
            Management
          </p>
          <h1 style={{ fontSize: 'clamp(28px, 3.4vw, 44px)' }}>Who knows…</h1>
          <p className="plomb" style={{ marginTop: 16, maxWidth: 600 }}>
            Search a technology, read the evidence, check the date. Level 3 and up carry written
            evidence — below that, the level stands alone. These are declarations, not an audit.
          </p>
        </div>
      </div>

      <form
        className="barre-actions"
        style={{ paddingBottom: 28 }}
        onSubmit={(e) => {
          e.preventDefault();
          setRequete(terme);
          const suivants = new URLSearchParams(parametres);
          suivants.set('techno', terme);
          setParametres(suivants, { replace: true });
        }}
      >
        <input
          className="saisie"
          style={{ maxWidth: 320 }}
          placeholder="Kafka, Airflow, Whisper…"
          value={terme}
          onChange={(e) => setTerme(e.target.value)}
          aria-label="Technology searched"
        />
        <select
          className="selection"
          style={{ width: 'auto' }}
          value={niveauMin}
          onChange={(e) => setNiveauMin(Number(e.target.value))}
          aria-label="Minimum level"
        >
          {/* 3 est le seuil de staffing — autonome, avec preuve écrite. Mais les
              niveaux 1 et 2 sont exactement ceux qu'un plan de formation cherche :
              des gens qui ont commencé et qu'on peut faire monter. */}
          <option value={0}>0 and above — everyone who answered</option>
          <option value={1}>1 and above — has some notion of it</option>
          <option value={2}>2 and above — has used it, supervised</option>
          <option value={3}>3 and above — autonomous, with evidence</option>
          <option value={4}>4 and above — advanced</option>
          <option value={5}>5 — reference</option>
        </select>
        <button type="submit" className="bouton bouton--primaire">
          Search
        </button>
      </form>

      {resultat.enCours ? <Chargement quoi="Searching" /> : null}
      {resultat.erreur ? <Message variante="erreur">{resultat.erreur.message}</Message> : null}

      {resultat.donnees?.technologies.length === 0 ? (
        <Message variante="info">
          Nobody has declared {niveauMin} or above on “{resultat.donnees.requete}” — which is
          information in itself. Lower the threshold to see who has at least touched it.
        </Message>
      ) : null}

      {(resultat.donnees?.technologies ?? []).map((t) => (
        <section key={t.technologieId} style={{ paddingBottom: 40 }}>
          <div className="entete-groupe">
            <div>
              <h2 style={{ fontSize: 'clamp(20px, 2.2vw, 28px)' }}>{t.titre}</h2>
              <span className="ligne__meta" style={{ marginTop: 6 }}>
                <Anneau anneau={t.anneau} />
                <span className="discret">{t.radar}</span>
              </span>
            </div>
            <span className="discret">
              {t.personnes.length} {t.personnes.length === 1 ? 'person' : 'people'}
            </span>
          </div>

          <div className="tableau-defilant">
            <table className="tableau">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Team</th>
                  <th className="tableau__nombre">Level</th>
                  <th>Evidence</th>
                  <th>Declared</th>
                </tr>
              </thead>
              <tbody>
                {t.personnes.map((p) => (
                  <tr key={p.personneId}>
                    <td>
                      <Link to={`/personnes/${p.personneId}`}>{p.nom}</Link>
                      <br />
                      <span className="discret">{p.email}</span>
                    </td>
                    <td>
                      <Equipes equipes={p.equipes} max={2} />
                    </td>
                    <td className="tableau__nombre" style={{ fontWeight: 500 }}>
                      {p.niveau}
                    </td>
                    <td style={{ maxWidth: 460, color: 'var(--fg-2)' }}>{p.preuve ?? '—'}</td>
                    <td className="discret" style={{ whiteSpace: 'nowrap' }}>
                      {p.trimestre}
                      <br />
                      {dateCourte(p.declareeLe)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
